import React, { useCallback } from 'react';

import { useStyles } from '~/assets/styles';
import Grid from '~/components/core/Atomic/Grid/Grid';
import Link from '~/components/core/Atomic/Link/Link';
import DatePickerField, { type PickerDate } from '~/components/core/Molecules/Fields/DatePickerField';
import MultiSelectField from '~/components/core/Molecules/Fields/MultiSelectField';
import CancelIcon from '~/components/icons/CancelIcon';
import cn from '~/Utils/cn';

import { RuleNameAutocomplete } from './RuleNameAutocomplete';
import { WorkflowNameAutocomplete } from './WorkflowNameAutocomplete';

interface FilterParams {
  workflowId: string;
  workflowName: string;
  ruleId: string;
  ruleName: string;
  status: string[];
  startDate: string;
  endDate: string;
}

interface WorkflowExecutionsFiltersProps {
  filterParams: FilterParams;
  onFilterChange: (params: Partial<FilterParams>) => void;
  onClearFilters: () => void;
  organizationId: number;
  statusCounts: Record<string, number>;
}

const STATUS_OPTIONS = [
  { value: 'PENDING', label: 'Pending' },
  { value: 'PROCESSING', label: 'Processing' },
  { value: 'SUCCESS', label: 'Success' },
  { value: 'FAILED', label: 'Failed' },
];

export const WorkflowExecutionsFilters: React.FC<WorkflowExecutionsFiltersProps> = ({
  filterParams,
  onFilterChange,
  onClearFilters,
  organizationId,
  statusCounts,
}) => {
  const { classes } = useStyles();

  const handleWorkflowNameChange = useCallback(
    (workflowId: string, workflowName: string) => {
      onFilterChange({ workflowId, workflowName });
    },
    [onFilterChange]
  );

  const handleRuleNameChange = useCallback(
    (ruleId: string, ruleName: string) => {
      onFilterChange({ ruleId, ruleName });
    },
    [onFilterChange]
  );

  const handleStatusChange = useCallback(
    (selectedStatuses: string[]) => {
      onFilterChange({ status: selectedStatuses });
    },
    [onFilterChange]
  );

  const handleStartDateChange = useCallback(
    (date: PickerDate) => {
      const dateString = date ? date.format('YYYY-MM-DD') : '';
      onFilterChange({ startDate: dateString });
    },
    [onFilterChange]
  );

  const handleEndDateChange = useCallback(
    (date: PickerDate) => {
      const dateString = date ? date.format('YYYY-MM-DD') : '';
      onFilterChange({ endDate: dateString });
    },
    [onFilterChange]
  );

  return (
    <>
      <Grid container spacing={3} className="pb-32">
        <Grid item xs={12} sm={6} md={3}>
          <WorkflowNameAutocomplete
            organizationId={organizationId}
            value={filterParams.workflowName}
            onChange={handleWorkflowNameChange}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <RuleNameAutocomplete
            organizationId={organizationId}
            value={filterParams.ruleName}
            onChange={handleRuleNameChange}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <MultiSelectField
            id="status"
            label="Status"
            className={classes.textField}
            fullWidth
            options={STATUS_OPTIONS.map((option) => option.value)}
            renderValue={(selected) => {
              if (selected.length === 0) {
                return 'All statuses';
              }
              return selected
                .map((status) => STATUS_OPTIONS.find((opt) => opt.value === status)?.label || status)
                .join(', ');
            }}
            renderOption={(status) => {
              const option = STATUS_OPTIONS.find((opt) => opt.value === status);
              const count = statusCounts[status] || 0;
              const isSelected = filterParams.status.includes(status);
              const hasNoSelection = filterParams.status.length === 0;

              // Show badge if:
              // 1. No statuses are selected (show all badges with count > 0), OR
              // 2. This status is selected AND has count > 0 (hide during loading when count is 0)
              const shouldShowBadge = (hasNoSelection && count > 0) || (isSelected && count > 0);

              return (
                <div className="flex w-full items-center justify-between">
                  <span>{option?.label || status}</span>
                  {shouldShowBadge && (
                    <span className="px-6 py-1 ml-8 min-w-[24px] rounded-full bg-slate-200 text-center text-xs font-medium text-slate-700">
                      {count}
                    </span>
                  )}
                </div>
              );
            }}
            onChange={handleStatusChange}
            value={filterParams.status}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <DatePickerField
            id="startDate"
            label="Start Date"
            className={classes.textField}
            disableFuture
            fullWidth
            clearable
            value={filterParams.startDate}
            onChange={handleStartDateChange}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <DatePickerField
            id="endDate"
            label="End Date"
            className={classes.textField}
            disableFuture
            fullWidth
            clearable
            value={filterParams.endDate}
            onChange={handleEndDateChange}
          />
        </Grid>
        <Grid item xs={12} md="auto" className="flex items-end justify-end">
          <Link
            href=""
            onClick={(e) => {
              e.preventDefault();
              onClearFilters();
            }}
            className={cn(classes.alignedContainerForGrid, classes.secondaryLink, 'pt-1.5')}
          >
            <CancelIcon className={cn(classes.filledIcon, classes.leftButtonIcon)} />
            Clear all filters
          </Link>
        </Grid>
      </Grid>
    </>
  );
};
